import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fetchLeagueFixtures, fetchLeagueTable } from "../api";
import { useAuth } from "../components/AuthGate";
import { normalizeWorldCupGroups } from "../utils/worldCup";
import styles from "../styles/WorldCupHub.module.css";

const STORAGE_KEY = "csb_world_cup_2026_entry_v1";
const FORMATIONS = ["4-3-3", "4-2-3-1", "3-4-3"];
const TABS = [
  ["bracket", "Bracket"],
  ["path", "Knockout Path"],
  ["dream", "Dream XI"],
  ["picks", "Matchday Picks"],
  ["sweep", "Sweepstake"],
  ["leagues", "Private Leagues"],
  ["share", "Share"],
];
const ROUND_LABELS = ["Round of 32", "Round of 16", "Quarter Finals", "Semi Finals", "Final"];

const emptyEntry = {
  groupPicks: {},
  thirdPlaceTeams: [],
  knockout: {},
  champion: "",
  goldenBoot: "",
  dreamMode: "dream",
  nation: "",
  formation: "4-3-3",
  dreamXI: {},
  matchdayPicks: {},
  leagues: [],
  shareCode: "",
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const normalizeRows = (payload) =>
  normalizeWorldCupGroups(payload).map((group) => ({
    name: group.name,
    rows: group.rows.map((row) => ({
      rank: row.position,
      id: row.team_id,
      name: row.team,
      logo: row.team_logo,
      group: row.group || group.name,
      points: row.points,
    })),
  }));

const normalizeFixtures = (payload) =>
  (payload?.fixtures || []).map((fixture) => ({
    id: firstDefined(fixture.csb_fixture_id, fixture.app_fixture_id, fixture.internal_fixture_id, fixture.id, fixture.fixture_id),
    providerId: firstDefined(fixture.provider_fixture_id, fixture.api_fixture_id),
    date: firstDefined(fixture.date, fixture.kickoff_utc, fixture.kickoff, fixture.timestamp),
    round: firstDefined(fixture.round, "World Cup"),
    home: firstDefined(fixture.home_team, fixture.home?.name, "Home"),
    away: firstDefined(fixture.away_team, fixture.away?.name, "Away"),
    venue: firstDefined(fixture.venue, fixture.venue_name, fixture.fixture?.venue?.name, ""),
  }));

const normalizePlayers = (payload) => {
  const rows = firstDefined(payload?.players, payload?.squad, payload?.items, payload?.response, payload, []);
  if (!Array.isArray(rows)) return [];
  return rows.map((row, idx) => {
    const player = row.player || row;
    const team = row.team || row.country || row.nation || {};
    const rawPosition = String(firstDefined(player.position, row.position, row.position_group, "")).toLowerCase();
    const position =
      rawPosition.includes("goal") || rawPosition === "gk" ? "goalkeeper" :
      rawPosition.includes("def") ? "defender" :
      rawPosition.includes("mid") ? "midfielder" :
      rawPosition.includes("forward") || rawPosition.includes("attacker") || rawPosition.includes("striker") || rawPosition === "fw" ? "forward" :
      normalizePositionGroup(row.position_group);
    return {
      id: String(firstDefined(player.id, row.player_id, row.id, `${player.name || row.name || "player"}-${idx}`)),
      name: firstDefined(player.name, row.player_name, row.name, "Unnamed player"),
      country: firstDefined(team.name, row.country_name, row.country, row.nation, ""),
      team: firstDefined(row.team?.name, row.team, team.name, row.country_name, row.country, row.nation, ""),
      provider_team_id: firstDefined(row.provider_team_id, row.team?.id, team.id, null),
      club: firstDefined(row.club, row.club_name, player.club, row.team?.club, ""),
      photo: firstDefined(row.photo, player.photo, row.image, player.image, ""),
      position,
    };
  }).filter((player) => player.name && player.position);
};

const normalizeSweepstakes = (payload) => {
  const rows = firstDefined(payload?.sweepstakes, payload?.rooms, payload?.items, payload?.response, payload, []);
  return Array.isArray(rows) ? rows : [];
};

const normalizeSweepstake = (payload) => firstDefined(payload?.sweepstake, payload?.room, payload?.data, payload, null);

const sweepstakeErrorMessage = (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) return "Sign in to create or join a World Cup sweepstake.";
  return firstDefined(error?.response?.data?.detail, error?.response?.data?.message, "Sweepstake action failed. Please try again.");
};

const assignmentTeams = (assignments = []) =>
  assignments
    .map((assignment) => (typeof assignment === "string" ? assignment : assignment?.team))
    .filter(Boolean)
    .join(", ");

const normalizeAssignmentList = (assignments = [], userId = null) =>
  (Array.isArray(assignments) ? assignments : []).map((assignment) =>
    typeof assignment === "string" ? { team: assignment, user_id: userId } : assignment
  );

const projectedPct = (row = {}) => {
  const value = Number(row.projected_win_pct);
  return Number.isFinite(value) && value > 0 ? value.toFixed(1) : "";
};

const strongestAssignment = (row = {}) => {
  const teams = normalizeAssignmentList(row.assignments, row.user_id);
  return teams.find((assignment) => assignment.team === row.strongest_team) || null;
};

const formatTeamList = (teams = []) => {
  const names = teams.map((team) => (typeof team === "string" ? team : team?.team)).filter(Boolean);
  if (!names.length) return "my World Cup teams";
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
};

const assignmentMeta = (assignment = {}) => {
  const bits = [];
  if (assignment.group_name) bits.push(assignment.group_name);
  if (assignment.pot !== undefined && assignment.pot !== null && assignment.pot !== "") bits.push(`Pot ${assignment.pot}`);
  if (assignment.fifa_rank !== undefined && assignment.fifa_rank !== null && assignment.fifa_rank !== "") bits.push(`FIFA #${assignment.fifa_rank}`);
  return bits.join(" / ");
};

const escapeSvg = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const createSweepstakeSvgFile = ({ roomName, memberName, teams, joinCode, projected, drawStrength, strongestTeam }) => {
  const headline = `I drew ${formatTeamList(teams)}`;
  const teamRows = teams.slice(0, 5).map((team, idx) => {
    const y = 380 + idx * 58;
    return `
      <rect x="74" y="${y}" width="932" height="46" rx="18" fill="rgba(255,255,255,0.09)" stroke="rgba(255,214,102,0.32)" />
      <text x="104" y="${y + 30}" fill="#ffffff" font-size="25" font-weight="800">${escapeSvg(team.team)}</text>
      <text x="760" y="${y + 30}" fill="#ffd666" font-size="20" font-weight="800">${escapeSvg(assignmentMeta(team))}</text>
    `;
  }).join("");
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="1080" height="1080" viewBox="0 0 1080 1080">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stop-color="#052b1a" />
          <stop offset="58%" stop-color="#071812" />
          <stop offset="100%" stop-color="#0b301d" />
        </linearGradient>
        <radialGradient id="gold" cx="20%" cy="0%" r="80%">
          <stop offset="0%" stop-color="#fff0a6" stop-opacity="0.45" />
          <stop offset="100%" stop-color="#ffd666" stop-opacity="0" />
        </radialGradient>
      </defs>
      <rect width="1080" height="1080" fill="url(#bg)" />
      <rect width="1080" height="1080" fill="url(#gold)" />
      <rect x="44" y="44" width="992" height="992" rx="42" fill="none" stroke="#ffd666" stroke-opacity="0.5" stroke-width="3" />
      <text x="74" y="128" fill="#ffd666" font-size="26" font-weight="900" letter-spacing="3">CSB WORLD CUP SWEEP</text>
      <text x="74" y="190" fill="#ffffff" font-size="54" font-weight="950">${escapeSvg(roomName || "World Cup Sweepstake")}</text>
      <text x="74" y="264" fill="#ffffff" font-size="44" font-weight="950">${escapeSvg(headline)}</text>
      <text x="74" y="304" fill="#b8c9bf" font-size="25" font-weight="700">${escapeSvg(memberName || "CSB member")}</text>
      ${projected ? `<text x="74" y="348" fill="#ffd666" font-size="29" font-weight="900">${escapeSvg(`${projected}% projected chance`)}</text>` : ""}
      ${drawStrength ? `<text x="390" y="348" fill="#ffffff" font-size="27" font-weight="850">${escapeSvg(drawStrength)}</text>` : ""}
      ${strongestTeam ? `<text x="690" y="348" fill="#b8c9bf" font-size="24" font-weight="800">${escapeSvg(`Best: ${strongestTeam}`)}</text>` : ""}
      ${teamRows}
      <rect x="74" y="842" width="932" height="96" rx="28" fill="#ffd666" />
      <text x="104" y="902" fill="#06150f" font-size="34" font-weight="950">Join code: ${escapeSvg(joinCode || "SWP")}</text>
      <text x="74" y="984" fill="#ffffff" fill-opacity="0.78" font-size="27" font-weight="800">charteredsportsbetting.com</text>
    </svg>
  `;
  return new File([new Blob([svg], { type: "image/svg+xml" })], "csb-world-cup-sweep.svg", { type: "image/svg+xml" });
};

const normalizeSquads = (payload) => {
  const teams = firstDefined(payload?.teams, payload?.squads, payload?.response, []);
  if (!Array.isArray(teams)) return [];
  return teams.flatMap((teamRow) => {
    const teamName = firstDefined(teamRow.team, teamRow.name, teamRow.team?.name, teamRow.country, "");
    const providerTeamId = firstDefined(teamRow.provider_team_id, teamRow.team_id, teamRow.team?.id, null);
    return (teamRow.players || teamRow.squad || []).map((player) => ({
      id: String(firstDefined(player.id, player.player_id, `${teamName}-${player.name}`)),
      name: firstDefined(player.name, player.player_name, "Unnamed player"),
      country: firstDefined(player.country, teamName),
      team: teamName,
      provider_team_id: providerTeamId,
      club: firstDefined(player.club, player.club_name, ""),
      photo: firstDefined(player.photo, player.image, ""),
      position: normalizePositionGroup(firstDefined(player.position_group, player.position, "")),
    }));
  }).filter((player) => player.name && player.position);
};

const normalizePositionGroup = (value = "") => {
  const raw = String(value).toLowerCase();
  if (raw.includes("goal") || raw === "gk") return "goalkeeper";
  if (raw.includes("def")) return "defender";
  if (raw.includes("mid")) return "midfielder";
  if (raw.includes("forward") || raw.includes("attacker") || raw.includes("striker") || raw === "fw") return "forward";
  return "";
};

const sortGroups = (groups) =>
  [...groups].sort((a, b) => {
    const av = groupRank(a.name);
    const bv = groupRank(b.name);
    if (av !== bv) return av - bv;
    return String(a.name).localeCompare(String(b.name));
  });

const groupRank = (name) => {
  const match = String(name || "").match(/^group\s+([a-l])$/i);
  return match ? match[1].toUpperCase().charCodeAt(0) - 65 : 99;
};

const stageGroupsOnly = (groups) => groups.filter((group) => /^group\s+[a-l]$/i.test(group.name));

const loadLocalEntry = () => {
  if (typeof window === "undefined") return emptyEntry;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? { ...emptyEntry, ...JSON.parse(raw) } : emptyEntry;
  } catch {
    return emptyEntry;
  }
};

const saveLocalEntry = (entry) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // ignore storage errors
  }
};

const dateLabel = (value) => {
  if (!value) return "TBD";
  const date = new Date(typeof value === "number" && value < 1e12 ? value * 1000 : value);
  if (Number.isNaN(date.getTime())) return "TBD";
  return date.toLocaleString("en-GB", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const formationLines = {
  "4-3-3": [
    ["FWD1", "FWD2", "FWD3"],
    ["MID1", "MID2", "MID3"],
    ["DEF1", "DEF2", "DEF3", "DEF4"],
    ["GK1"],
  ],
  "4-2-3-1": [
    ["FWD1"],
    ["MID1", "MID2", "MID3"],
    ["MID4", "MID5"],
    ["DEF1", "DEF2", "DEF3", "DEF4", "GK1"],
  ],
  "3-4-3": [
    ["FWD1", "FWD2", "FWD3"],
    ["MID1", "MID2", "MID3", "MID4"],
    ["DEF1", "DEF2", "DEF3"],
    ["GK1"],
  ],
};

const slotPosition = (slot) => {
  if (slot.startsWith("GK")) return "goalkeeper";
  if (slot.startsWith("DEF")) return "defender";
  if (slot.startsWith("MID")) return "midfielder";
  return "forward";
};

const positionLabel = (position) => ({
  goalkeeper: "Goalkeeper",
  defender: "Defender",
  midfielder: "Midfielder",
  forward: "Forward",
}[position] || "Player");

function buildBracketTeams(entry, groups) {
  const groupTeams = [];
  groups.forEach((group) => {
    const picks = entry.groupPicks[group.name] || {};
    if (picks.winner) groupTeams.push(picks.winner);
    if (picks.runnerUp) groupTeams.push(picks.runnerUp);
  });
  entry.thirdPlaceTeams.forEach((team) => {
    if (team) groupTeams.push(team);
  });
  return [...new Set(groupTeams)].slice(0, 32);
}

function roundSize(roundIdx) {
  return [16, 8, 4, 2, 1][roundIdx] || 1;
}

function WorldCupHub({ embedded = false } = {}) {
  const { shareCode: routeShareCode } = useParams();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("bracket");
  const [entry, setEntry] = useState(() => loadLocalEntry());
  const [groups, setGroups] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [players, setPlayers] = useState([]);
  const [communitySummary, setCommunitySummary] = useState(null);
  const [saveState, setSaveState] = useState("");
  const [pickerSlot, setPickerSlot] = useState(null);
  const [leagueDraft, setLeagueDraft] = useState({ name: "", joinCode: "" });

  useEffect(() => {
    let cancelled = false;
    async function loadData() {
      const [table, fixturePayload, entryPayload, summary, squadsPayload] = await Promise.all([
        fetchLeagueTable("WC", 2026).catch(() => null),
        fetchLeagueFixtures("WC", 2026).catch(() => null),
        routeShareCode
          ? api.get(`/api/world-cup/brackets/${encodeURIComponent(routeShareCode)}`).then((r) => r.data).catch(() => null)
          : api.get("/api/world-cup/entry").then((r) => r.data).catch(() => null),
        api.get("/api/world-cup/brackets/community-summary").then((r) => r.data).catch(() => null),
        api.get("/api/world-cup/squads").then((r) => r.data).catch(() => null),
      ]);
      if (cancelled) return;
      const parsedGroups = sortGroups(stageGroupsOnly(normalizeRows(table)));
      setGroups(parsedGroups);
      setFixtures(normalizeFixtures(fixturePayload));
      setPlayers(normalizeSquads(squadsPayload));
      setCommunitySummary(summary);
      if (entryPayload && typeof entryPayload === "object") {
        const loadedEntry = firstDefined(entryPayload.entry, entryPayload.bracket, entryPayload);
        setEntry((prev) => ({ ...prev, ...loadedEntry, shareCode: firstDefined(entryPayload.share_code, loadedEntry.shareCode, routeShareCode, prev.shareCode) }));
      }
    }
    loadData();
    return () => {
      cancelled = true;
    };
  }, [routeShareCode]);

  useEffect(() => {
    saveLocalEntry(entry);
  }, [entry]);

  const teams = useMemo(() => groups.flatMap((group) => group.rows), [groups]);
  const playerPool = useMemo(() => players, [players]);
  const champion = entry.champion || entry.knockout?.finalWinner || "";
  const finalMatchup = [entry.knockout?.semi1Winner, entry.knockout?.semi2Winner].filter(Boolean).join(" vs ") || "Final matchup pending";
  const shareCode = entry.shareCode || `WC26-${String((champion || "CSB").replace(/[^a-z0-9]/gi, "")).slice(0, 6).toUpperCase() || "ENTRY"}`;

  const updateEntry = useCallback((patch) => {
    setEntry((prev) => ({ ...prev, ...patch }));
  }, []);

  const saveEntry = useCallback(async () => {
    setSaveState("Saving...");
    const payload = { ...entry, shareCode };
    saveLocalEntry(payload);
    try {
      const res = await api.put("/api/world-cup/entry", payload);
      setEntry((prev) => ({ ...prev, ...(res.data || payload), shareCode }));
      setSaveState("Saved");
    } catch {
      setEntry((prev) => ({ ...prev, shareCode }));
      setSaveState("Saved locally");
    }
    window.setTimeout(() => setSaveState(""), 1800);
  }, [entry, shareCode]);

  const createShare = useCallback(async () => {
    const payload = { ...entry, shareCode };
    await saveEntry();
    try {
      const res = await api.post("/api/world-cup/brackets/share", payload);
      const nextCode = firstDefined(res.data?.share_code, res.data?.shareCode, res.data?.code, shareCode);
      const nextUrl = firstDefined(res.data?.url, res.data?.share_url, typeof window !== "undefined" ? `${window.location.origin}/world-cup/brackets/${encodeURIComponent(nextCode)}` : "");
      setEntry((prev) => ({ ...prev, shareCode: nextCode }));
      return { code: nextCode, url: nextUrl };
    } catch {
      const nextUrl = typeof window !== "undefined" ? `${window.location.origin}/world-cup/brackets/${encodeURIComponent(shareCode)}` : "";
      return { code: shareCode, url: nextUrl };
    }
  }, [entry, saveEntry, shareCode]);

  const shareText = `CSB World Cup 2026: my champion is ${champion || "still undecided"}. Final: ${finalMatchup}. Dream XI: ${Object.values(entry.dreamXI || {}).map((p) => p?.name).filter(Boolean).slice(0, 4).join(", ") || "in progress"}.`;
  const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/world-cup/brackets/${encodeURIComponent(shareCode)}` : "";

  const content = (
    <>
      {!embedded ? (
        <section className={styles.hero}>
          <div className={styles.heroMain}>
            <div className={styles.logoBox}>
              <img
                src="/world-cup-2026-logo.jpeg"
                alt="FIFA World Cup 2026 logo"
                className={styles.brandLogo}
              />
            </div>
            <div>
              <div className={styles.kicker}>CSB Fan Hub / United States / Mexico / Canada</div>
              <h1 className={styles.title}>World Cup 2026</h1>
              <p className={styles.sub}>Build your bracket, pick your champion, create a Dream XI, set up private leagues, and share your tournament card.</p>
            </div>
          </div>
          <div className={styles.heroActions}>
            <div className={styles.spotlight}>
              <span>My winner</span>
              <strong>{champion || "Pick a champion"}</strong>
            </div>
            <div className={styles.statCard}><span>Groups</span><strong>{groups.length || "-"}</strong></div>
            <div className={styles.statCard}><span>Fixtures</span><strong>{fixtures.length || "-"}</strong></div>
          </div>
        </section>
      ) : (
        <section className={styles.embeddedIntro}>
          <div>
            <div className={styles.kicker}>CSB Fan Hub</div>
            <h2>Make your bracket, Dream XI, private leagues, and share cards.</h2>
          </div>
          <div className={styles.buttonRow}>
            <span className={styles.miniStat}>{champion || "Champion pending"}</span>
            <span className={styles.miniStat}>{players.length ? `${players.length} players` : "Final squads pending"}</span>
          </div>
        </section>
      )}

      <nav className={styles.tabs}>
        {TABS.map(([key, label]) => (
          <button key={key} type="button" className={`${styles.tab} ${activeTab === key ? styles.activeTab : ""}`} onClick={() => setActiveTab(key)}>
            {label}
          </button>
        ))}
      </nav>

      {!user ? (
        <div className={styles.softPrompt}>
          You can build and share locally while signed out. Sign in when you want your bracket, Dream XI, and private leagues synced across devices.
        </div>
      ) : null}

      {activeTab === "bracket" && (
        <BracketTab
          groups={groups}
          teams={teams}
          entry={entry}
          updateEntry={updateEntry}
          saveEntry={saveEntry}
          saveState={saveState}
          communitySummary={communitySummary}
          players={players}
        />
      )}
      {activeTab === "path" && (
        <KnockoutPathTab entry={entry} groups={groups} updateEntry={updateEntry} />
      )}
      {activeTab === "dream" && (
        <DreamXITab
          entry={entry}
          updateEntry={updateEntry}
          groups={groups}
          playerPool={playerPool}
          pickerSlot={pickerSlot}
          setPickerSlot={setPickerSlot}
          hasRealPlayers={players.length > 0}
        />
      )}
      {activeTab === "picks" && (
        <MatchdayPicksTab fixtures={fixtures} entry={entry} updateEntry={updateEntry} />
      )}
      {activeTab === "leagues" && (
        <PrivateLeaguesTab
          fixtures={fixtures}
          entry={entry}
          updateEntry={updateEntry}
          leagueDraft={leagueDraft}
          setLeagueDraft={setLeagueDraft}
        />
      )}
      {activeTab === "sweep" && (
        <SweepstakeTab user={user} />
      )}
      {activeTab === "share" && (
        <ShareTab
          champion={champion}
          finalMatchup={finalMatchup}
          entry={entry}
          shareText={shareText}
          shareUrl={shareUrl}
          shareCode={shareCode}
          createShare={createShare}
        />
      )}

      {!embedded ? (
        <div style={{ marginTop: 16 }}>
          <Link className={styles.ghostButton} to="/league/WC">Official fixtures and groups</Link>
        </div>
      ) : null}

      {pickerSlot ? (
        <PlayerPicker
          slot={pickerSlot}
          entry={entry}
          updateEntry={updateEntry}
          playerPool={playerPool}
          onClose={() => setPickerSlot(null)}
        />
      ) : null}
    </>
  );

  if (embedded) return <div className={styles.embedded}>{content}</div>;

  return (
    <div className={styles.page}>
      <div className={styles.shell}>
        {content}
      </div>
    </div>
  );
}

function BracketTab({ groups, teams, entry, updateEntry, saveEntry, saveState, communitySummary, players }) {
  const selectedWinners = new Set(Object.values(entry.groupPicks || {}).flatMap((pick) => [pick.winner, pick.runnerUp]).filter(Boolean));
  const thirdOptions = teams.filter((team) => !selectedWinners.has(team.name));
  const champion = entry.champion || "";

  const setGroupPick = (groupName, field, value) => {
    const current = entry.groupPicks[groupName] || {};
    const next = { ...current, [field]: value };
    if (field === "winner" && next.runnerUp === value) next.runnerUp = "";
    if (field === "runnerUp" && next.winner === value) next.winner = "";
    updateEntry({ groupPicks: { ...entry.groupPicks, [groupName]: next } });
  };

  return (
    <div className={styles.grid}>
      <div className={styles.panel}>
        <div className={styles.panelHeader}>
          <h2>Group Picks</h2>
          <span>Winners / runners-up / best third-place</span>
        </div>
        <div className={styles.panelBody}>
          {!groups.length ? <div className={styles.emptyState}>Groups are loading from the official World Cup standings endpoint.</div> : null}
          <div className={styles.groupGrid}>
            {groups.map((group) => {
              const picks = entry.groupPicks[group.name] || {};
              return (
                <div key={group.name} className={styles.groupCard}>
                  <div className={styles.groupTitle}>
                    <span>{group.name}</span>
                    <span>{group.rows.length} teams</span>
                  </div>
                  <div className={styles.pickRow}>
                    <label className={styles.label}>Winner
                      <select className={styles.select} value={picks.winner || ""} onChange={(e) => setGroupPick(group.name, "winner", e.target.value)}>
                        <option value="">Pick winner</option>
                        {group.rows.map((team) => <option key={team.name} value={team.name}>{team.name}</option>)}
                      </select>
                    </label>
                    <label className={styles.label}>Runner-up
                      <select className={styles.select} value={picks.runnerUp || ""} onChange={(e) => setGroupPick(group.name, "runnerUp", e.target.value)}>
                        <option value="">Pick runner-up</option>
                        {group.rows.filter((team) => team.name !== picks.winner).map((team) => <option key={team.name} value={team.name}>{team.name}</option>)}
                      </select>
                    </label>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 14 }} className={styles.panel}>
            <div className={styles.panelHeader}><h2>Best third-place qualifiers</h2><span>Pick up to 8</span></div>
            <div className={styles.panelBody}>
              <div className={styles.groupGrid}>
                {Array.from({ length: 8 }).map((_, idx) => (
                  <label key={idx} className={styles.label}>Third-place slot {idx + 1}
                    <select
                      className={styles.select}
                      value={entry.thirdPlaceTeams[idx] || ""}
                      onChange={(e) => {
                        const next = [...entry.thirdPlaceTeams];
                        next[idx] = e.target.value;
                        updateEntry({ thirdPlaceTeams: [...new Set(next.filter(Boolean))] });
                      }}
                    >
                      <option value="">Placeholder</option>
                      {thirdOptions.map((team) => <option key={team.name} value={team.name}>{team.name}</option>)}
                    </select>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.stack}>
        <div className={styles.panel}>
          <div className={styles.panelHeader}><h2>My winner</h2><span>Champion pick</span></div>
          <div className={styles.panelBody}>
            <label className={styles.label}>Champion
              <select className={styles.select} value={champion} onChange={(e) => updateEntry({ champion: e.target.value })}>
                <option value="">Pick champion</option>
                {teams.map((team) => <option key={team.name} value={team.name}>{team.name}</option>)}
              </select>
            </label>
            <label className={styles.label} style={{ marginTop: 12 }}>Golden Boot
              <select className={styles.select} value={entry.goldenBoot || ""} onChange={(e) => updateEntry({ goldenBoot: e.target.value })}>
                <option value="">Pick golden boot</option>
                {players
                  .filter((player) => player.position === "forward")
                  .map((player) => (
                    <option key={player.id} value={player.name}>{player.name} ({player.country})</option>
                  ))}
              </select>
            </label>
            {!players.length ? (
              <div style={{ marginTop: 10 }} className={styles.emptyState}>Golden Boot player list appears when final squads are loaded.</div>
            ) : null}
            <div style={{ marginTop: 12 }} className={styles.shareCard}>
              <div className={styles.kicker}>CSB World Cup 2026</div>
              <h3>{champion || "Champion pending"}</h3>
              <div className={styles.shareMeta}>
                <span>{communitySummary?.champion_percent ? `${communitySummary.champion_percent}% of CSB users also picked this winner.` : "Community comparison unlocks when summary data is available."}</span>
                <span>{communitySummary?.spice_percentile ? `Your bracket is spicier than ${communitySummary.spice_percentile}% of entries.` : "Spice score pending."}</span>
              </div>
            </div>
            <div style={{ marginTop: 12 }} className={styles.buttonRow}>
              <button type="button" className={styles.goldButton} onClick={saveEntry}>Save picks</button>
              {saveState ? <span style={{ alignSelf: "center", color: "#ffd666", fontWeight: 800 }}>{saveState}</span> : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function KnockoutPathTab({ entry, groups, updateEntry }) {
  const teams = buildBracketTeams(entry, groups);
  const rounds = ROUND_LABELS.map((label, roundIdx) => {
    const matches = Array.from({ length: roundSize(roundIdx) });
    return { label, roundIdx, matches };
  });

  const teamForSlot = (roundIdx, matchIdx, side) => {
    if (roundIdx === 0) return teams[matchIdx * 2 + side] || "";
    return entry.knockout?.[`r${roundIdx - 1}m${matchIdx * 2 + side}`] || "";
  };

  const setWinner = (roundIdx, matchIdx, team) => {
    const key = roundIdx === 4 ? "finalWinner" : `r${roundIdx}m${matchIdx}`;
    updateEntry({
      knockout: { ...entry.knockout, [key]: team },
      champion: roundIdx === 4 ? team : entry.champion,
    });
  };

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}><h2>Knockout Path</h2><span>Round of 32 to Final</span></div>
      <div className={styles.panelBody}>
        {!teams.length ? <div className={styles.emptyState}>Pick group winners, runners-up, and third-place qualifiers to unlock the path.</div> : null}
        <div className={styles.knockoutGrid}>
          {rounds.map((round) => (
            <div key={round.label} className={styles.roundColumn}>
              <div className={styles.roundTitle}>{round.label}</div>
              {round.matches.map((_, matchIdx) => {
                const a = teamForSlot(round.roundIdx, matchIdx, 0);
                const b = teamForSlot(round.roundIdx, matchIdx, 1);
                const winnerKey = round.roundIdx === 4 ? "finalWinner" : `r${round.roundIdx}m${matchIdx}`;
                const picked = entry.knockout?.[winnerKey] || "";
                return (
                  <div key={matchIdx} className={styles.matchCard}>
                    {[a, b].map((team, side) => (
                      team ? (
                        <button key={side} type="button" className={`${styles.teamButton} ${picked === team ? styles.selectedTeam : ""}`} onClick={() => setWinner(round.roundIdx, matchIdx, team)}>
                          {team}
                        </button>
                      ) : (
                        <div key={side} className={styles.teamSlot}>Placeholder</div>
                      )
                    ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DreamXITab({ entry, updateEntry, groups, pickerSlot, setPickerSlot, hasRealPlayers }) {
  const nations = groups.flatMap((group) => group.rows.map((team) => team.name));
  const lines = formationLines[entry.formation] || formationLines["4-3-3"];

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}><h2>Dream XI</h2><span>Pitch selector</span></div>
      <div className={styles.panelBody}>
        <div className={styles.pitchWrap}>
          <div className={styles.pitch}>
            {lines.map((line, idx) => (
              <div key={idx} className={styles.line}>
                {line.map((slot) => {
                  const player = entry.dreamXI?.[slot];
                  return (
                    <button key={slot} type="button" className={styles.playerSlot} onClick={() => setPickerSlot(slot)}>
                      <strong>{player?.name || positionLabel(slotPosition(slot))}</strong>
                      <span>{player?.country || "Tap to pick"}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className={styles.sidePanel}>
            <label className={styles.label}>Mode
              <select className={styles.select} value={entry.dreamMode} onChange={(e) => updateEntry({ dreamMode: e.target.value, dreamXI: {} })}>
                <option value="dream">Dream XI</option>
                <option value="nation">My Nation XI</option>
              </select>
            </label>
            {entry.dreamMode === "nation" ? (
              <label className={styles.label}>Nation
                <select className={styles.select} value={entry.nation || ""} onChange={(e) => updateEntry({ nation: e.target.value, dreamXI: {} })}>
                  <option value="">Choose country</option>
                  {nations.map((nation) => <option key={nation} value={nation}>{nation}</option>)}
                </select>
              </label>
            ) : null}
            <label className={styles.label}>Formation
              <select className={styles.select} value={entry.formation} onChange={(e) => updateEntry({ formation: e.target.value, dreamXI: {} })}>
                {FORMATIONS.map((formation) => <option key={formation} value={formation}>{formation}</option>)}
              </select>
            </label>
            <div className={styles.emptyState}>
              {hasRealPlayers
                ? "Squad data is live. Search by player, country, or club and avoid duplicate selections."
                : "Final squads not loaded yet. Manual entries are available as a fallback."}
            </div>
            <button type="button" className={styles.ghostButton} onClick={() => updateEntry({ dreamXI: {} })}>Clear XI</button>
          </div>
        </div>
      </div>
      {pickerSlot ? null : null}
    </div>
  );
}

function PlayerPicker({ slot, entry, updateEntry, playerPool, onClose }) {
  const [search, setSearch] = useState("");
  const [remotePlayers, setRemotePlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const position = slotPosition(slot);
  const selectedIds = new Set(Object.values(entry.dreamXI || {}).map((player) => player?.id).filter(Boolean));
  useEffect(() => {
    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get("/api/world-cup/players", {
          params: {
            position,
            q: search,
          },
        });
        if (!cancelled) setRemotePlayers(normalizePlayers(res.data));
      } catch {
        if (!cancelled) setRemotePlayers([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [position, search]);

  const sourcePool = remotePlayers.length ? remotePlayers : playerPool;
  const pool = sourcePool
    .filter((player) => player.position === position)
    .filter((player) => entry.dreamMode !== "nation" || !entry.nation || player.country === entry.nation)
    .filter((player) => `${player.name} ${player.country} ${player.club}`.toLowerCase().includes(search.toLowerCase()))
    .slice(0, 80);

  const choosePlayer = (player) => {
    updateEntry({ dreamXI: { ...entry.dreamXI, [slot]: player } });
    onClose();
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{positionLabel(position)}s</h2>
          <button type="button" className={styles.ghostButton} onClick={onClose}>Close</button>
        </div>
        <div className={styles.modalBody}>
          <input className={styles.input} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search player, country, club" />
          {loading ? <div className={styles.emptyState}>Loading players...</div> : null}
          <div className={styles.playerList}>
            {pool.map((player) => (
              <button key={player.id} type="button" className={styles.playerPick} disabled={selectedIds.has(player.id)} onClick={() => choosePlayer(player)}>
                {player.photo ? <img src={player.photo} alt="" className={styles.playerPhoto} onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
                <span>
                  <strong>{player.name}</strong>
                  <em>{player.country || player.team || "Country TBD"}{player.club ? ` / ${player.club}` : ""}</em>
                </span>
              </button>
            ))}
          </div>
          {!pool.length ? (
            <>
              <div className={styles.emptyState}>Final squads not loaded yet.</div>
              <div className={styles.buttonRow}>
                <button type="button" className={styles.goldButton} onClick={() => choosePlayer({ id: `manual-${slot}-${Date.now()}`, name: search || "Manual pick", country: entry.nation || "Custom", club: "Manual entry", position })}>
                  Use manual entry
                </button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function MatchdayPicksTab({ fixtures, entry, updateEntry }) {
  const shown = fixtures.slice(0, 24);
  const setPick = (fixture, value) => {
    updateEntry({ matchdayPicks: { ...entry.matchdayPicks, [fixture.id || fixture.providerId]: value } });
  };

  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}><h2>Matchday Picks</h2><span>Pick winners</span></div>
      <div className={styles.panelBody}>
        {!shown.length ? <div className={styles.emptyState}>Fixtures are loading from the World Cup competition endpoint.</div> : null}
        <div className={styles.stack}>
          {shown.map((fixture) => {
            const key = fixture.id || fixture.providerId || `${fixture.home}-${fixture.away}`;
            const picked = entry.matchdayPicks?.[key] || "";
            return (
              <div key={key} className={styles.fixturePickCard}>
                <div>
                  <strong>{fixture.home} vs {fixture.away}</strong>
                  <div style={{ color: "rgba(255,255,255,.62)", fontSize: 13 }}>{dateLabel(fixture.date)} / {fixture.venue || fixture.round}</div>
                </div>
                <select className={styles.select} value={picked} onChange={(e) => setPick(fixture, e.target.value)}>
                  <option value="">Pick</option>
                  <option value={fixture.home}>{fixture.home}</option>
                  <option value="Draw">Draw</option>
                  <option value={fixture.away}>{fixture.away}</option>
                </select>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function PrivateLeaguesTab({ fixtures, entry, updateEntry, leagueDraft, setLeagueDraft }) {
  const createLeague = async () => {
    const code = `WC${Math.random().toString(36).slice(2, 7).toUpperCase()}`;
    const league = {
      id: code,
      name: leagueDraft.name || "My World Cup League",
      code,
      members: ["You"],
      fixtures: fixtures.slice(0, 7).map((fixture) => fixture.id || fixture.providerId || `${fixture.home}-${fixture.away}`),
    };
    try {
      const res = await api.post("/api/world-cup/leagues", league);
      updateEntry({ leagues: [res.data || league, ...(entry.leagues || [])] });
    } catch {
      updateEntry({ leagues: [league, ...(entry.leagues || [])] });
    }
    setLeagueDraft({ name: "", joinCode: "" });
  };

  const joinLeague = async () => {
    const code = leagueDraft.joinCode.trim().toUpperCase();
    if (!code) return;
    try {
      const res = await api.post("/api/world-cup/leagues/join", { code });
      updateEntry({ leagues: [res.data || { id: code, name: `League ${code}`, code, members: ["You"], fixtures: [] }, ...(entry.leagues || [])] });
    } catch {
      updateEntry({ leagues: [{ id: code, name: `League ${code}`, code, members: ["You"], fixtures: [] }, ...(entry.leagues || [])] });
    }
    setLeagueDraft({ name: "", joinCode: "" });
  };

  return (
    <div className={styles.grid}>
      <div className={styles.panel}>
        <div className={styles.panelHeader}><h2>Create Private League</h2><span>Up to 7 fixtures</span></div>
        <div className={styles.panelBody}>
          <div className={styles.stack}>
            <label className={styles.label}>League name
              <input className={styles.input} value={leagueDraft.name} onChange={(e) => setLeagueDraft((prev) => ({ ...prev, name: e.target.value }))} placeholder="Office sweepstake, mates league..." />
            </label>
            <button type="button" className={styles.goldButton} onClick={createLeague}>Create league</button>
            <div className={styles.emptyState}>Creator fixtures default to the next seven World Cup fixtures. Backend scoring can fill standings when ready.</div>
          </div>
        </div>
      </div>
      <div className={styles.panel}>
        <div className={styles.panelHeader}><h2>Join League</h2><span>Code or link</span></div>
        <div className={styles.panelBody}>
          <div className={styles.buttonRow}>
            <input className={styles.input} value={leagueDraft.joinCode} onChange={(e) => setLeagueDraft((prev) => ({ ...prev, joinCode: e.target.value }))} placeholder="Join code" />
            <button type="button" className={styles.primaryButton} onClick={joinLeague}>Join</button>
          </div>
        </div>
      </div>
      {(entry.leagues || []).map((league) => (
        <div key={league.id || league.code} className={styles.leagueCard}>
          <h3>{league.name}</h3>
          <p>Join code: <strong>{league.code}</strong></p>
          <p>Members: {(league.members || []).join(", ") || "You"}</p>
          <p style={{ color: "rgba(255,255,255,.66)" }}>Standings will appear when backend scoring is available.</p>
        </div>
      ))}
    </div>
  );
}

function SweepstakeTab({ user }) {
  const [rooms, setRooms] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionState, setActionState] = useState("");
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({ name: "", max_members: 12, draw_mode: "tiered" });
  const [joinCode, setJoinCode] = useState("");
  const [guestNames, setGuestNames] = useState("");
  const [revealIndex, setRevealIndex] = useState(0);
  const [showFullDraw, setShowFullDraw] = useState(false);

  const loadRooms = useCallback(async (preferredId = "") => {
    if (!user) {
      setRooms([]);
      setDetail(null);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/api/world-cup/sweepstakes");
      const nextRooms = normalizeSweepstakes(res.data);
      setRooms(nextRooms);
      const nextSelected = preferredId || nextRooms[0]?.id || "";
      setSelectedId(nextSelected);
      if (nextSelected) {
        const detailRes = await api.get(`/api/world-cup/sweepstakes/${encodeURIComponent(nextSelected)}`);
        setDetail(normalizeSweepstake(detailRes.data));
      } else {
        setDetail(null);
      }
    } catch (err) {
      setError(sweepstakeErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadRooms();
  }, [loadRooms]);

  const openRoom = async (roomId) => {
    if (!roomId) return;
    setSelectedId(roomId);
    setActionState("Opening...");
    setError("");
    try {
      const res = await api.get(`/api/world-cup/sweepstakes/${encodeURIComponent(roomId)}`);
      setDetail(normalizeSweepstake(res.data));
    } catch (err) {
      setError(sweepstakeErrorMessage(err));
    } finally {
      setActionState("");
    }
  };

  const createRoom = async () => {
    if (!draft.name.trim()) {
      setError("Give your sweepstake room a name first.");
      return;
    }
    setActionState("Creating...");
    setError("");
    try {
      const res = await api.post("/api/world-cup/sweepstakes", {
        name: draft.name.trim(),
        competition_key: "world_cup_2026",
        max_members: Number(draft.max_members) || 12,
        draw_mode: draft.draw_mode || "tiered",
      });
      const next = normalizeSweepstake(res.data);
      setDraft({ name: "", max_members: 12, draw_mode: "tiered" });
      setDetail(next);
      setSelectedId(next?.id || "");
      await loadRooms(next?.id || "");
      setActionState("Created");
    } catch (err) {
      setError(sweepstakeErrorMessage(err));
    } finally {
      window.setTimeout(() => setActionState(""), 1400);
    }
  };

  const joinRoom = async () => {
    const code = joinCode.trim().toUpperCase();
    if (!code) {
      setError("Enter a sweepstake join code first.");
      return;
    }
    setActionState("Joining...");
    setError("");
    try {
      const res = await api.post("/api/world-cup/sweepstakes/join", { join_code: code });
      const next = normalizeSweepstake(res.data);
      setJoinCode("");
      setDetail(next);
      setSelectedId(next?.id || "");
      await loadRooms(next?.id || "");
      setActionState("Joined");
    } catch (err) {
      setError(sweepstakeErrorMessage(err));
    } finally {
      window.setTimeout(() => setActionState(""), 1400);
    }
  };

  const addGuestMembers = async () => {
    if (!detail?.id || !detail?.is_owner || detail?.status !== "open") return;
    const names = guestNames
      .split(/\n|,/)
      .map((name) => name.trim())
      .filter(Boolean);
    if (!names.length) {
      setError("Add at least one name first.");
      return;
    }
    setActionState("Adding names...");
    setError("");
    try {
      const res = await api.post(`/api/world-cup/sweepstakes/${encodeURIComponent(detail.id)}/members`, { names });
      const next = normalizeSweepstake(res.data);
      setGuestNames("");
      setDetail(next);
      await loadRooms(next?.id || detail.id);
      setActionState("Names added");
    } catch (err) {
      setError(sweepstakeErrorMessage(err));
    } finally {
      window.setTimeout(() => setActionState(""), 1400);
    }
  };

  const drawTeams = async () => {
    if (!detail?.id || !detail?.is_owner || detail?.status !== "open") return;
    setActionState("Drawing...");
    setError("");
    try {
      const res = await api.post(`/api/world-cup/sweepstakes/${encodeURIComponent(detail.id)}/draw`);
      const next = normalizeSweepstake(res.data);
      setDetail(next);
      await loadRooms(next?.id || detail.id);
      setActionState("Teams assigned");
    } catch (err) {
      setError(sweepstakeErrorMessage(err));
    } finally {
      window.setTimeout(() => setActionState(""), 1400);
    }
  };

  const copyText = async (text) => {
    if (!text) return;
    await navigator.clipboard?.writeText(text);
    setActionState("Copied");
    window.setTimeout(() => setActionState(""), 1200);
  };

  const shareSweepstake = async () => {
    if (!detail) return;
    const url = firstDefined(detail.share_url, detail.app_url, typeof window !== "undefined" ? `${window.location.origin}/world-cup` : "");
    const text = `Join my CSB World Cup 2026 sweepstake: ${detail.name}. Code: ${detail.join_code}`;
    if (navigator.share) {
      await navigator.share({ title: detail.name, text, url });
    } else {
      await copyText(`${text}\n${url}`);
    }
  };

  const members = useMemo(() => Array.isArray(detail?.members) ? detail.members : [], [detail?.members]);
  const assignments = useMemo(() => Array.isArray(detail?.assignments) ? detail.assignments : [], [detail?.assignments]);
  const leaderboard = useMemo(() => Array.isArray(detail?.leaderboard) ? detail.leaderboard : [], [detail?.leaderboard]);
  const currentUserId = firstDefined(detail?.current_user_id, user?.id, user?.uid);
  const isOpen = detail?.status === "open";
  const isDrawn = detail?.status === "drawn" || assignments.length > 0;
  const canDraw = Boolean(detail?.is_owner && isOpen && Number(detail?.member_count || members.length) >= 2);
  const currentMember = members.find((member) => member.user_id !== null && member.user_id !== undefined && String(member.user_id) === String(currentUserId));
  const currentLeaderboardRow = leaderboard.find((row) => row.user_id !== null && row.user_id !== undefined && String(row.user_id) === String(currentUserId));
  const directMyAssignments = assignments.filter((assignment) => String(assignment.user_id) === String(currentUserId));
  const leaderboardAssignments = normalizeAssignmentList(currentLeaderboardRow?.assignments, currentUserId);
  const myAssignments = directMyAssignments.length ? directMyAssignments : leaderboardAssignments;
  const myName = firstDefined(currentLeaderboardRow?.name, currentMember?.name, user?.displayName, user?.email, "You");
  const myProjected = projectedPct(currentLeaderboardRow);
  const myStrength = currentLeaderboardRow?.draw_strength || "";
  const myBestTeam = currentLeaderboardRow?.strongest_team || "";
  const shareDrawText = `I drew ${formatTeamList(myAssignments)} in the CSB World Cup Sweepstake.${myProjected ? ` ${myProjected}% projected chance.` : ""} Join code: ${detail?.join_code || ""}`;
  const assignmentsByMember = assignments.reduce((acc, assignment) => {
    const key = assignment.member_key || assignment.user_id || assignment.member_name || "member";
    if (!acc[key]) acc[key] = { name: assignment.member_name || "Member", rows: [] };
    acc[key].rows.push(assignment);
    return acc;
  }, {});

  const shareDrawCard = async () => {
    if (!detail) return;
    const url = firstDefined(detail.share_url, typeof window !== "undefined" ? `${window.location.origin}/world-cup?sweepstake=${encodeURIComponent(detail.join_code || "")}` : "");
    const sharePayload = {
      title: "CSB World Cup Sweep",
      text: shareDrawText,
      url,
    };
    try {
      if (typeof File !== "undefined" && navigator.share && navigator.canShare) {
        const file = createSweepstakeSvgFile({
          roomName: detail.name,
          memberName: myName,
          teams: myAssignments,
          joinCode: detail.join_code,
          projected: myProjected,
          drawStrength: myStrength,
          strongestTeam: myBestTeam,
        });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ ...sharePayload, files: [file] });
          return;
        }
      }
      if (navigator.share) {
        await navigator.share(sharePayload);
      } else {
        await copyText(`${shareDrawText}\n${url}`);
      }
    } catch (err) {
      if (err?.name !== "AbortError") await copyText(`${shareDrawText}\n${url}`);
    }
  };

  const revealRows = useMemo(() => {
    if (!isDrawn) return [];
    const byMember = assignments.reduce((acc, assignment) => {
      const key = String(assignment.member_key || assignment.user_id || assignment.member_name || "member");
      if (!acc[key]) acc[key] = [];
      acc[key].push(assignment);
      return acc;
    }, {});
    const memberJoinOrder = members.reduce((acc, member, idx) => {
      acc[String(member.member_key || member.user_id || member.name)] = idx;
      return acc;
    }, {});
    const sortRevealRows = (a, b) => {
      const ar = a.rank ?? null;
      const br = b.rank ?? null;
      if (ar !== null || br !== null) return firstDefined(ar, 999) - firstDefined(br, 999);
      return firstDefined(memberJoinOrder[String(a.member_key || a.user_id || a.name)], 999) - firstDefined(memberJoinOrder[String(b.member_key || b.user_id || b.name)], 999);
    };
    const sourceRows = leaderboard.length
      ? [...leaderboard].sort(sortRevealRows)
      : [...members].sort((a, b) => firstDefined(memberJoinOrder[String(a.member_key || a.user_id || a.name)], 999) - firstDefined(memberJoinOrder[String(b.member_key || b.user_id || b.name)], 999));
    return sourceRows.map((row, idx) => {
      const userId = row.user_id;
      const rowAssignments = normalizeAssignmentList(row.assignments, userId);
      return {
        user_id: userId,
        member_key: row.member_key,
        name: row.name || `Member ${idx + 1}`,
        role: row.role,
        rank: row.rank,
        points: row.points,
        projected_win_pct: row.projected_win_pct,
        draw_strength: row.draw_strength,
        strongest_team: row.strongest_team,
        isCurrentUser: String(userId) === String(currentUserId),
        assignments: rowAssignments.length ? rowAssignments : (byMember[String(row.member_key || userId)] || []),
      };
    }).filter((row) => row.assignments.length);
  }, [assignments, currentUserId, isDrawn, leaderboard, members]);

  useEffect(() => {
    setRevealIndex(0);
    setShowFullDraw(false);
  }, [detail?.id, detail?.drawn_at, detail?.status]);

  const shareRevealRow = async (row) => {
    if (!detail || !row) return;
    const pct = projectedPct(row);
    const text = `${row.name} drew ${formatTeamList(row.assignments)} in the CSB World Cup Sweepstake.${pct ? ` ${pct}% projected chance.` : ""} Join code: ${detail.join_code || ""}`;
    const url = firstDefined(detail.share_url, typeof window !== "undefined" ? `${window.location.origin}/world-cup?sweepstake=${encodeURIComponent(detail.join_code || "")}` : "");
    if (navigator.share) {
      await navigator.share({ title: "CSB World Cup Sweep", text, url });
    } else {
      await copyText(`${text}\n${url}`);
    }
  };

  if (!user) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHeader}><h2>World Cup Sweepstake</h2><span>Sign in</span></div>
        <div className={styles.panelBody}>
          <div className={styles.sweepHero}>
            <div>
              <div className={styles.kicker}>Draw teams with your mates before kickoff.</div>
              <h3>Sign in to create or join a World Cup sweepstake.</h3>
              <p>Rooms, codes, members, team assignments, and leaderboard tracking sync to your CSB account.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.sweepLayout}>
      <div className={styles.stack}>
        <div className={styles.sweepHero}>
          <div>
            <div className={styles.kicker}>World Cup Sweepstake</div>
            <h3>Draw teams with your mates before kickoff.</h3>
            <p>Tiered draw spreads the giants and long shots fairly. Share a code, fill the room, then let the creator draw once everyone is in.</p>
          </div>
          <div className={styles.trophyBadge}>26</div>
        </div>

        <div className={styles.panel}>
          <div className={styles.panelHeader}><h2>Create room</h2><span>{draft.draw_mode === "random" ? "Random draw" : "Tiered draw"}</span></div>
          <div className={styles.panelBody}>
            <div className={styles.stack}>
              <label className={styles.label}>Room name
                <input className={styles.input} value={draft.name} onChange={(e) => setDraft((prev) => ({ ...prev, name: e.target.value }))} placeholder="Office World Cup Sweep" />
              </label>
              <label className={styles.label}>Max members
                <input className={styles.input} type="number" min="2" max="48" value={draft.max_members} onChange={(e) => setDraft((prev) => ({ ...prev, max_members: e.target.value }))} />
              </label>
              <div className={styles.drawStyleBlock}>
                <div className={styles.label}>Draw style</div>
                <div className={styles.drawStyleGrid}>
                  <button
                    type="button"
                    className={`${styles.drawStyleCard} ${draft.draw_mode === "tiered" ? styles.activeDrawStyle : ""}`}
                    onClick={() => setDraft((prev) => ({ ...prev, draw_mode: "tiered" }))}
                  >
                    <strong>Tiered draw</strong>
                    <span>Balanced and fair. Spreads favourites, mid-tier teams and outsiders across the room.</span>
                  </button>
                  <button
                    type="button"
                    className={`${styles.drawStyleCard} ${draft.draw_mode === "random" ? styles.activeDrawStyle : ""}`}
                    onClick={() => setDraft((prev) => ({ ...prev, draw_mode: "random" }))}
                  >
                    <strong>Random draw</strong>
                    <span>Pure chaos. Every team is shuffled with no balancing.</span>
                  </button>
                </div>
              </div>
              <button type="button" className={styles.goldButton} onClick={createRoom} disabled={actionState === "Creating..."}>Create sweepstake</button>
            </div>
          </div>
        </div>

        <div className={styles.panel}>
          <div className={styles.panelHeader}><h2>Join room</h2><span>Code</span></div>
          <div className={styles.panelBody}>
            <div className={styles.buttonRow}>
              <input className={styles.input} value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="SWP-ABC123" />
              <button type="button" className={styles.primaryButton} onClick={joinRoom} disabled={actionState === "Joining..."}>Join</button>
            </div>
          </div>
        </div>

        <div className={styles.panel}>
          <div className={styles.panelHeader}><h2>My sweepstakes</h2><span>{rooms.length} rooms</span></div>
          <div className={styles.panelBody}>
            {loading ? <div className={styles.emptyState}>Loading your sweepstake rooms...</div> : null}
            {!loading && !rooms.length ? <div className={styles.emptyState}>No sweepstake rooms yet. Create one, copy the code, and get the group chat moving.</div> : null}
            <div className={styles.roomList}>
              {rooms.map((room) => (
                <button key={room.id} type="button" className={`${styles.roomButton} ${selectedId === room.id ? styles.activeRoomButton : ""}`} onClick={() => openRoom(room.id)}>
                  <span>
                    <strong>{room.name}</strong>
                    <em>{room.member_count || 0}/{room.max_members || 12} joined</em>
                  </span>
                  <b>{room.status || "open"}</b>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={styles.stack}>
        {error ? <div className={styles.emptyState}>{error}</div> : null}
        {actionState ? <div className={styles.miniStat}>{actionState}</div> : null}
        {!detail ? (
          <div className={styles.panel}>
            <div className={styles.panelHeader}><h2>Sweepstake room</h2><span>Pick a room</span></div>
            <div className={styles.panelBody}>
              <div className={styles.emptyState}>Open a room to see join code, members, team draw, and leaderboard.</div>
            </div>
          </div>
        ) : (
          <>
            <div className={styles.panel}>
              <div className={styles.panelHeader}>
                <h2>{detail.name}</h2>
                <span>{isDrawn ? "Teams assigned" : "Open room"}</span>
              </div>
              <div className={styles.panelBody}>
                <div className={styles.sweepStats}>
                  <span className={styles.miniStat}>{Number(detail.member_count || members.length)}/{detail.max_members || 12} joined</span>
                  <span className={styles.miniStat}>{detail.draw_mode || "tiered"} draw</span>
                  <span className={styles.miniStat}>{detail.assignment_count || assignments.length} teams</span>
                </div>
                <div className={styles.joinCodeCard}>
                  <span>Share this code so friends can join</span>
                  <strong>{detail.join_code || "Code pending"}</strong>
                  <div className={styles.buttonRow}>
                    <button type="button" className={styles.goldButton} onClick={() => copyText(detail.join_code)}>Copy code</button>
                    <button type="button" className={styles.primaryButton} onClick={isDrawn ? shareDrawCard : shareSweepstake}>{isDrawn ? "Share my draw" : "Share invite"}</button>
                    {isDrawn && revealRows.length ? <button type="button" className={styles.ghostButton} onClick={() => setShowFullDraw(true)}>View full draw</button> : null}
                    {firstDefined(detail.share_url, detail.app_url) ? (
                      <button type="button" className={styles.ghostButton} onClick={() => copyText(firstDefined(detail.share_url, detail.app_url))}>Copy link</button>
                    ) : null}
                  </div>
                </div>
                {isOpen ? (
                  <div className={styles.drawBanner}>
                    <strong>{detail.is_owner ? "Creator controls" : "Waiting room"}</strong>
                    <span>{detail.is_owner ? "Tiered draw spreads the giants and long shots fairly. Draw teams once everyone is in." : "Waiting for the creator to draw teams."}</span>
                    {detail.is_owner ? <button type="button" className={styles.goldButton} onClick={drawTeams} disabled={!canDraw}>{canDraw ? "Draw teams" : "Draw teams"}</button> : null}
                    {detail.is_owner && !canDraw ? <em>At least two people need to join before drawing.</em> : null}
                  </div>
                ) : null}
                {isOpen && detail.is_owner ? (
                  <div className={styles.manualMembersBox}>
                    <div>
                      <strong>Add names manually</strong>
                      <span>Add friends, office sweep entrants, or pub names without making them log in.</span>
                    </div>
                    <textarea
                      className={styles.textarea}
                      value={guestNames}
                      onChange={(event) => setGuestNames(event.target.value)}
                      placeholder={"Sam\nJess\nDad\nTable 4"}
                      rows={4}
                    />
                    <button type="button" className={styles.primaryButton} onClick={addGuestMembers} disabled={actionState === "Adding names..."}>Add names</button>
                  </div>
                ) : null}
              </div>
            </div>

            {!isDrawn ? (
              <div className={styles.panel}>
                <div className={styles.panelHeader}><h2>Members</h2><span>The creator draws once everyone is in</span></div>
                <div className={styles.panelBody}>
                  {!members.length ? <div className={styles.emptyState}>No members shown yet.</div> : null}
                  <div className={styles.memberGrid}>
                    {members.map((member) => (
                      <div key={`${member.member_key || member.user_id || member.name}-${member.joined_at}`} className={styles.memberPill}>
                        <strong>{member.name || "Member"}</strong>
                        <span>{member.guest_name ? "named entry" : (member.role || "member")} / {dateLabel(member.joined_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {!showFullDraw && revealRows.length ? (
                  <SweepstakeRevealStage
                    rows={revealRows}
                    index={Math.min(revealIndex, Math.max(revealRows.length - 1, 0))}
                    onNext={() => {
                      if (revealIndex >= revealRows.length - 1) setShowFullDraw(true);
                      else setRevealIndex((idx) => idx + 1);
                    }}
                    onSkip={() => setShowFullDraw(true)}
                    onShare={shareRevealRow}
                  />
                ) : (
                  <>
                    <MyDrawCard
                      detail={detail}
                      memberName={myName}
                      assignments={myAssignments}
                      projectedPct={myProjected}
                      drawStrength={myStrength}
                      strongestTeam={myBestTeam}
                      strongestTeamLogo={strongestAssignment(currentLeaderboardRow)?.team_logo}
                      shareText={shareDrawText}
                      onShare={shareDrawCard}
                    />

                    <div className={styles.panel}>
                      <div className={styles.panelHeader}><h2>Leaderboard</h2><span>Let the bragging begin</span></div>
                      <div className={styles.panelBody}>
                        {!leaderboard.length ? <div className={styles.emptyState}>Leaderboard appears when scoring starts.</div> : null}
                        <div className={styles.leaderboardRows}>
                          {leaderboard.map((row) => (
                            <LeaderboardRow key={row.member_key || row.user_id || row.rank} row={row} />
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className={styles.panel}>
                      <div className={styles.panelHeader}><h2>Assigned teams</h2><span>Teams are locked</span></div>
                      <div className={styles.panelBody}>
                        {!assignments.length ? <div className={styles.emptyState}>Teams are assigned, but no assignment rows came back yet.</div> : null}
                        <div className={styles.assignmentGroups}>
                          {Object.entries(assignmentsByMember).map(([memberKey, member]) => (
                            <div key={memberKey} className={styles.memberAssignment}>
                              <h3>{member.name}</h3>
                              <div className={styles.assignmentGrid}>
                                {member.rows.map((assignment) => (
                                  <div key={assignment.id || `${assignment.user_id}-${assignment.team}`} className={styles.assignmentCard}>
                                    {assignment.team_logo ? <img src={assignment.team_logo} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : <span className={styles.logoPlaceholder}>WC</span>}
                                    <div>
                                      <strong>{assignment.team}</strong>
                                      {assignmentMeta(assignment) ? <span>{assignmentMeta(assignment)}</span> : null}
                                      <em>{assignment.member_name || member.name}</em>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SweepstakeRevealStage({ rows, index, onNext, onSkip, onShare }) {
  const [phase, setPhase] = useState("up");
  const row = rows[index] || rows[0];
  const isLast = index >= rows.length - 1;

  useEffect(() => {
    setPhase("up");
    const timers = [
      window.setTimeout(() => setPhase("drawing"), 5000),
      window.setTimeout(() => setPhase("envelope"), 10000),
      window.setTimeout(() => setPhase("revealed"), 15000),
    ];
    return () => timers.forEach((timer) => window.clearTimeout(timer));
  }, [index]);

  if (!row) {
    return (
      <div className={styles.panel}>
        <div className={styles.panelHeader}><h2>Live draw</h2><span>Teams assigned</span></div>
        <div className={styles.panelBody}>
          <div className={styles.emptyState}>No reveal rows were returned yet. View the full draw to inspect assignments.</div>
          <button type="button" className={styles.goldButton} onClick={onSkip}>View full draw</button>
        </div>
      </div>
    );
  }

  const statusText =
    phase === "up" ? `${row.name} is up...` :
    phase === "drawing" ? "Drawing teams..." :
    phase === "envelope" ? "Opening envelope..." :
    "Teams revealed";
  const pct = projectedPct(row);

  return (
    <div className={styles.revealStage}>
      <div className={styles.revealTopline}>
        <span>Live World Cup draw</span>
        <button type="button" className={styles.ghostButton} onClick={onSkip}>View full draw</button>
      </div>
      <div className={styles.revealPerson}>
        {row.isCurrentUser ? <span className={styles.currentReveal}>Your draw</span> : null}
        <h2>{statusText}</h2>
        <p>{index + 1} of {rows.length}</p>
      </div>
      <div className={`${styles.envelope} ${phase === "revealed" ? styles.openEnvelope : ""}`}>
        <div className={styles.envelopeSeal}>26</div>
      </div>
      {phase === "revealed" ? (
        <>
        <div className={styles.drawContext}>
          {pct ? (
            <span className={styles.projectedPill}>
              {pct}% projected chance
              <span className={styles.infoTip} tabIndex="0">i<span>Projected chance is based on your assigned teams' pre-tournament strength, using FIFA rank, squad value and CSB team ratings. It is not bookmaker odds.</span></span>
            </span>
          ) : null}
          {row.draw_strength ? <span className={styles.strengthBadge}>{row.draw_strength}</span> : null}
          {row.strongest_team ? <span className={styles.bestTeamText}>Best team: {row.strongest_team}</span> : null}
        </div>
        <div className={styles.revealTeams}>
          {row.assignments.map((assignment, teamIdx) => (
            <div key={assignment.id || `${row.user_id}-${assignment.team}-${teamIdx}`} className={styles.revealTeamCard}>
              {assignment.team_logo ? <img src={assignment.team_logo} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : <span className={styles.logoPlaceholder}>WC</span>}
              <div>
                <strong>{assignment.team}</strong>
                {assignmentMeta(assignment) ? <span>{assignmentMeta(assignment)}</span> : null}
              </div>
            </div>
          ))}
        </div>
        </>
      ) : (
        <div className={styles.revealHolding}>
          <span>{phase === "envelope" ? "Sealed teams inside" : "Stand by for the draw"}</span>
        </div>
      )}
      <div className={styles.buttonRow}>
        <button type="button" className={styles.goldButton} onClick={() => phase === "revealed" ? onNext() : setPhase("revealed")}>
          {phase === "revealed" ? (isLast ? "Finish reveal" : "Reveal next") : "Reveal now"}
        </button>
        <button type="button" className={styles.primaryButton} onClick={() => onShare(row)}>Share this draw</button>
        <button type="button" className={styles.ghostButton} onClick={onSkip}>Skip to full draw</button>
      </div>
    </div>
  );
}

function MyDrawCard({ detail, memberName, assignments, projectedPct: pct, drawStrength, strongestTeam, strongestTeamLogo, shareText, onShare }) {
  const drawnTeams = assignments || [];
  return (
    <div className={styles.myDrawPanel}>
      <div className={styles.myDrawCard}>
        <div className={styles.myDrawTopline}>
          <span>CSB World Cup Sweep</span>
          <b>{detail?.join_code || "Code pending"}</b>
        </div>
        <div className={styles.myDrawRoom}>{detail?.name || "World Cup Sweepstake"}</div>
        <h2>I drew {formatTeamList(drawnTeams)}</h2>
        <p>{memberName || "You"}</p>
        <div className={styles.drawContext}>
          {pct ? (
            <span className={styles.projectedPill}>
              Projected chance: {pct}%
              <span className={styles.infoTip} tabIndex="0">i<span>Projected chance is based on your assigned teams' pre-tournament strength, using FIFA rank, squad value and CSB team ratings. It is not bookmaker odds.</span></span>
            </span>
          ) : null}
          {drawStrength ? <span className={styles.strengthBadge}>{drawStrength}</span> : null}
          {strongestTeam ? (
            <span className={styles.bestTeamText}>
              {strongestTeamLogo ? <img src={strongestTeamLogo} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
              Best team: {strongestTeam}
            </span>
          ) : null}
        </div>
        {drawnTeams.length ? (
          <div className={styles.myDrawTeams}>
            {drawnTeams.map((assignment) => (
              <div key={assignment.id || `${assignment.team}-${assignment.pot}`} className={styles.myDrawTeam}>
                {assignment.team_logo ? <img src={assignment.team_logo} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : <span className={styles.logoPlaceholder}>WC</span>}
                <div>
                  <strong>{assignment.team}</strong>
                  {assignmentMeta(assignment) ? <span>{assignmentMeta(assignment)}</span> : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>Your teams were not returned yet. Refresh the room once the draw completes.</div>
        )}
        <div className={styles.myDrawFooter}>
          <span>charteredsportsbetting.com</span>
          <button type="button" className={styles.goldButton} onClick={onShare}>Share my draw</button>
        </div>
      </div>
      <div className={styles.shareCaption}>
        {shareText}
      </div>
    </div>
  );
}

function LeaderboardRow({ row }) {
  const pct = projectedPct(row);
  const teams = assignmentTeams(row.assignments);
  const best = strongestAssignment(row);
  return (
    <div className={styles.leaderRow}>
      <div>
        <strong>#{row.rank || "-"} {row.name || "Member"}</strong>
        <div className={styles.leaderStats}>
          <span>{row.points || 0} pts</span>
          {pct ? (
            <span className={styles.projectedPill}>
              {pct}% projected
              <span className={styles.infoTip} tabIndex="0">i<span>Projected chance is based on your assigned teams' pre-tournament strength, using FIFA rank, squad value and CSB team ratings. It is not bookmaker odds.</span></span>
            </span>
          ) : null}
          {row.draw_strength ? <span className={styles.strengthBadge}>{row.draw_strength}</span> : null}
        </div>
        {row.strongest_team ? (
          <div className={styles.bestTeamText}>
            {best?.team_logo ? <img src={best.team_logo} alt="" onError={(event) => { event.currentTarget.style.display = "none"; }} /> : null}
            Best team: {row.strongest_team}
          </div>
        ) : null}
      </div>
      <span>{teams || "Teams pending"}</span>
    </div>
  );
}

function ShareTab({ champion, finalMatchup, entry, shareText, shareUrl, shareCode, createShare }) {
  const dreamSummary = Object.values(entry.dreamXI || {}).map((p) => p?.name).filter(Boolean).slice(0, 11);
  const [shareResult, setShareResult] = useState({ code: shareCode, url: shareUrl });
  const copy = async (text) => {
    await navigator.clipboard?.writeText(text);
  };
  const nativeShare = async () => {
    const result = await createShare();
    setShareResult(result);
    if (navigator.share) {
      await navigator.share({ title: "CSB World Cup 2026", text: shareText, url: result.url });
    } else {
      await copy(`${shareText}\n${result.url}`);
    }
  };
  const ensureShare = async () => {
    const result = await createShare();
    setShareResult(result);
    return result;
  };

  return (
    <div className={styles.grid}>
      <div className={styles.shareCard}>
        <div className={styles.kicker}>CSB World Cup 2026</div>
        <h3>{champion || "Champion pending"}</h3>
        <div className={styles.shareMeta}>
          <span>Final: {finalMatchup}</span>
          <span>Dream XI: {dreamSummary.length ? dreamSummary.join(", ") : "Build your XI"}</span>
          <span>Golden Boot: {entry.goldenBoot || "Pending"}</span>
          <span>Share code: {shareResult.code || shareCode}</span>
          <span>charteredsportsbetting.com</span>
        </div>
      </div>
      <div className={styles.panel}>
        <div className={styles.panelHeader}><h2>Share</h2><span>Social-ready</span></div>
        <div className={styles.panelBody}>
          <div className={styles.stack}>
            <button type="button" className={styles.goldButton} onClick={nativeShare}>Share Card</button>
            <button type="button" className={styles.primaryButton} onClick={async () => copy((await ensureShare()).url)}>Copy Link</button>
            <button type="button" className={styles.ghostButton} onClick={() => copy(shareText)}>Copy Text</button>
            <div className={styles.emptyState}>If native share is unavailable, Share Card copies your link and caption.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default WorldCupHub;
